/**
 * Typed axios wrapper. Single source of truth for HTTP calls to the Sherlock backend.
 * Auth token is read from localStorage on every request; 401s clear the token and
 * redirect to /login.
 */

import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';

const TOKEN_KEY = 'sherlock_token';

export interface ErrorResponse {
  detail: string;
}

export class ApiError extends Error {
  status: number;
  detail: string;
  constructor(status: number, detail: string) {
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

class ApiHelper {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_URL || '',
      timeout: 30000,
      // Cross-origin in prod (SPA on `sherlock.…`, API on `sherlock-api.…`).
      // The session cookie is scoped to `.detect-inspections.com` so the
      // browser will attach it on cross-subdomain requests, but only when
      // `withCredentials` is set. Locally the requests are same-origin via the
      // Vite dev proxy, so this is a no-op.
      withCredentials: true,
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ErrorResponse>) => {
        const status = error.response?.status ?? 0;
        const detail = error.response?.data?.detail ?? error.message;
        if (status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          if (window.location.pathname !== '/login') {
            window.location.assign('/login');
          }
        }
        return Promise.reject(new ApiError(status, detail));
      },
    );
  }

  setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
  }

  clearToken(): void {
    localStorage.removeItem(TOKEN_KEY);
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  async get<T>(url: string, params?: Record<string, unknown>, config?: AxiosRequestConfig): Promise<T> {
    const resp = await this.client.get<T>(url, { ...config, params });
    return resp.data;
  }

  async post<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const resp = await this.client.post<T>(url, body, config);
    return resp.data;
  }

  async put<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const resp = await this.client.put<T>(url, body, config);
    return resp.data;
  }

  async patch<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const resp = await this.client.patch<T>(url, body, config);
    return resp.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const resp = await this.client.delete<T>(url, config);
    return resp.data;
  }
}

export const apiHelper = new ApiHelper();

// ---------- typed convenience methods ----------

export interface MeResponse {
  id: string;
  email: string;
  name: string;
  picture_url: string;
  is_engineer: boolean;
}

export const auth = {
  me: () => apiHelper.get<MeResponse>('/api/auth/me'),
  devLogin: (email: string) =>
    apiHelper.post<{ token: string; email: string }>('/api/auth/dev/login', { email }),
};

// ---------- issues ----------

import type { PaginatedIssues, IssueCounts } from '@/types/issue';
import type { AnalysisState } from '@/types/issue';

export interface ListIssuesParams {
  status?: string;
  level?: string;
  analysis_state?: AnalysisState;
  /** Registry project_key — resolved server-side to Sentry project slugs. */
  project?: string;
  page?: number;
}

export interface IssueCountsParams {
  status?: string;
  level?: string;
  project?: string;
}

export const issues = {
  list: (params: ListIssuesParams = {}) =>
    apiHelper.get<PaginatedIssues>('/api/issues/', params as Record<string, unknown>),
  counts: (params: IssueCountsParams = {}) =>
    apiHelper.get<IssueCounts>('/api/issues/counts', params as Record<string, unknown>),
};

// ---------- projects (shared registry) ----------

import type { PaginatedProjects } from '@/types/project';

export const projects = {
  list: () => apiHelper.get<PaginatedProjects>('/api/projects/'),
};

// ---------- insights ----------

import type { CostSummary, DashboardData } from '@/types/insights';

export const insights = {
  dashboard: () => apiHelper.get<DashboardData>('/api/insights/dashboard/'),
  costSummary: (from: string, to: string) =>
    apiHelper.get<CostSummary>(`/api/insights/cost/?from=${from}&to=${to}`),
};

// ---------- analyses ----------

import type { AnalysisGetResponse, AnalysisModel, AnalysisQueued, PromptVersion } from '@/types/analysis';

export interface CancelAnalysisResponse {
  id: string;
  status: 'queued' | 'running';
  cancelled_at: string;  // ISO 8601
}

export const analyses = {
  request: (
    issueId: string,
    model: AnalysisModel = 'claude-sonnet-4-6',
    promptVersion: PromptVersion = 'auto_triage_agentic.v1',
    force = false,
  ) =>
    apiHelper.post<AnalysisQueued>(`/api/issues/${issueId}/analyse/`, {
      model,
      prompt_version: promptVersion,
      force,
    }),

  get: (issueId: string) =>
    apiHelper.get<AnalysisGetResponse>(`/api/issues/${issueId}/analysis/`),

  /** POST /api/analyses/{analysis_id}/cancel/ — request cancellation of a running/queued analysis. */
  cancel: (analysisId: string) =>
    apiHelper.post<CancelAnalysisResponse>(`/api/analyses/${analysisId}/cancel/`),

  /**
   * Build the SSE URL for the analysis event stream.
   * Auth is passed via ?token= because EventSource cannot set headers.
   * @param issueId - optional — if present, filters events to a single issue
   */
  streamUrl: (issueId?: string): string => {
    const base = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
    const token = localStorage.getItem(TOKEN_KEY);
    const params = new URLSearchParams();
    if (token) params.set('token', token);
    if (issueId) params.set('issue_id', issueId);
    return `${base}/api/analyses/stream?${params.toString()}`;
  },
};

// ---------- service health ----------

import type { ServiceHealth } from '@/types/health';

export const health = {
  listServices: () => apiHelper.get<ServiceHealth[]>('/api/health/services/'),
};

// ---------- status (sidebar footer) ----------

import type { StatusResponse } from '@/types/status';

export const status = {
  get: () => apiHelper.get<StatusResponse>('/api/status/'),
};

// ---------- log events ----------

import type { PaginatedLogEvents } from '@/types/log_event';

export interface ListLogEventsParams {
  service?: string;
  level?: string;
  since?: string;
  page?: number;
}

export const logEvents = {
  list: (params: ListLogEventsParams = {}) =>
    apiHelper.get<PaginatedLogEvents>('/api/log-events/', params as Record<string, unknown>),
};

// ---------- share ----------

import type {
  SharedRca,
  ShareListRow,
  ShareMintRequest,
  ShareMintResponse,
} from '@/types/share';

export interface ListSharesParams {
  /** When 'active', revoked + expired tokens are excluded. */
  status?: 'active';
  page?: number;
}

export interface PaginatedShares {
  count: number;
  next: string | null;
  previous: string | null;
  results: ShareListRow[];
}

/**
 * publicApi — bare axios instance WITHOUT the auth interceptor.
 * Used for public (token-in-path) endpoints like GET /api/share/rca/{token}/
 * so that we never accidentally send the user's JWT for unauthenticated routes
 * and never trigger the 401 redirect logic on a 404.
 */
const publicApiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  timeout: 30000,
  withCredentials: false,
});

publicApiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ErrorResponse>) => {
    const status = error.response?.status ?? 0;
    const detail = error.response?.data?.detail ?? error.message;
    return Promise.reject(new ApiError(status, detail));
  },
);

/**
 * Public (no-auth) share endpoints.
 * The token in the path is the credential — no JWT header required.
 */
export const publicShare = {
  /**
   * Fetch the shared RCA payload by token.
   * Backend: GET /api/share/rca/{token}/  (token-only, no JWT)
   */
  getByToken: async (token: string): Promise<SharedRca> => {
    const resp = await publicApiClient.get<SharedRca>(`/api/share/rca/${token}/`);
    return resp.data;
  },
};

export const share = {
  /**
   * Mint a share for a completed analysis.
   * Backend: POST /api/analyses/{analysis_id}/share/  (JWT)
   */
  mintForAnalysis: (analysisId: string, body: ShareMintRequest = {}) =>
    apiHelper.post<ShareMintResponse>(`/api/analyses/${analysisId}/share/`, body),

  /**
   * Mint a share for a finished investigation.
   * Backend: POST /api/investigations/{investigation_id}/share/  (JWT)
   */
  mintForInvestigation: (investigationId: string, body: ShareMintRequest = {}) =>
    apiHelper.post<ShareMintResponse>(
      `/api/investigations/${investigationId}/share/`,
      body,
    ),

  /**
   * Revoke a share. 403 unless caller is the creator. 204 on success.
   * Backend: DELETE /api/share/{token}/  (JWT)
   */
  revoke: (token: string) => apiHelper.delete<void>(`/api/share/${token}/`),

  /**
   * List the caller's shares (paginated).
   * Backend: GET /api/share/  (JWT)
   */
  list: (params: ListSharesParams = {}) =>
    apiHelper.get<PaginatedShares>('/api/share/', params as Record<string, unknown>),
};

// ---------- investigate ----------

import type {
  ClarificationRequiredResponse,
  InvestigateRequest,
  InvestigationAuthorsResponse,
  InvestigationDetail,
  InvestigationStatus,
  PaginatedInvestigations,
  SSEEvent,
} from '@/types/investigation';

export interface ListInvestigationsParams {
  page?: number;
  // Mirrors the backend Query() params on GET /api/investigations/.
  status?: InvestigationStatus;
  user_id?: string;
  q?: string;
  // When true, scope to the current user. Overrides user_id on the server.
  mine?: boolean;
  // Admin-only: filter by soft-delete state. Non-admin callers always see
  // "active" regardless of the value sent. Default "active".
  deleted?: 'active' | 'deleted' | 'all';
}

/**
 * Two modes for the SSE stream:
 *
 *  - `live`: POST /api/investigate/ with the supplied body. Used by the
 *    /investigate page after the user submits the form.
 *  - `catchup`: GET /api/investigations/{id}/stream. Used by the detail
 *    page to replay persisted events and (if the row is still running)
 *    tail the live broker channel.
 *
 * Both modes share the same SSE event union and the same parser.
 */
export type InvestigateStreamRequest =
  | { mode: 'live'; body: InvestigateRequest }
  | { mode: 'catchup'; investigationId: string };

/**
 * SSE handlers. The browser EventSource API doesn't support custom
 * headers (no `Authorization: Bearer ...`), so we use fetch() +
 * ReadableStream + a tiny SSE frame parser. Each
 * `event: <name>\ndata: <json>\n\n` block becomes one SSEEvent emitted
 * to `onEvent`.
 *
 * Pre-stream HTTP errors (401, 404, 422, 429) come through `onHttpError`
 * with {status, detail}. Stream-mid errors go to `onStreamError`.
 * `onComplete` fires once when the stream closes (success or failure).
 */
export interface InvestigateStreamHandlers {
  onEvent: (event: SSEEvent) => void;
  onHttpError?: (err: { status: number; detail: string }) => void;
  onStreamError?: (err: Error) => void;
  onComplete?: () => void;
  /**
   * Pre-flight clarification gate (post-Sprint-12). The backend returns
   * HTTP 202 with this payload when the prompt is too vague to investigate
   * efficiently. ``onComplete`` still fires after this callback so the
   * caller's loading state can wind down. Only fires on ``mode: 'live'``
   * requests — the catchup path is for already-persisted investigations.
   */
  onClarificationRequired?: (payload: ClarificationRequiredResponse) => void;
}

export const investigate = {
  list: (params: ListInvestigationsParams = {}) => {
    // Drop empty/undefined values so the URL stays tidy (?status=&q= → bad
    // request, the backend would treat "" as a literal status). Booleans
    // serialize as "true"/"false"; falsy `mine` is dropped entirely.
    const cleaned: Record<string, unknown> = {};
    if (params.page !== undefined && params.page > 0) cleaned.page = params.page;
    if (params.status) cleaned.status = params.status;
    if (params.user_id) cleaned.user_id = params.user_id;
    if (params.q && params.q.trim()) cleaned.q = params.q.trim();
    if (params.mine) cleaned.mine = 'true';
    // Only include `deleted` when non-default; "active" is the server default.
    if (params.deleted && params.deleted !== 'active') cleaned.deleted = params.deleted;
    return apiHelper.get<PaginatedInvestigations>('/api/investigations/', cleaned);
  },

  listAuthors: () =>
    apiHelper.get<InvestigationAuthorsResponse>('/api/investigations/authors/'),

  get: (id: string) =>
    apiHelper.get<InvestigationDetail>(`/api/investigations/${id}/`),

  /**
   * Soft-delete an investigation. Returns void on 204.
   * Backend: DELETE /api/investigations/{id}/  (JWT required)
   */
  delete: (id: string): Promise<void> =>
    apiHelper.delete<void>(`/api/investigations/${id}/`),

  /**
   * Open the SSE stream and pump events into the supplied handlers.
   * Returns a teardown function that aborts the in-flight fetch.
   */
  stream: (
    request: InvestigateStreamRequest,
    handlers: InvestigateStreamHandlers,
  ): (() => void) => {
    const controller = new AbortController();
    const baseUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
    const token = localStorage.getItem(TOKEN_KEY);

    void (async () => {
      const init: RequestInit = {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: 'text/event-stream',
        },
        // Cross-origin SSE in prod — the session cookie lives on
        // `.detect-inspections.com` and only travels with credentialed
        // requests. Without this the stream would 401 on the new subdomain.
        credentials: 'include',
        signal: controller.signal,
      };
      let url: string;
      if (request.mode === 'live') {
        url = `${baseUrl}/api/investigate/`;
        init.method = 'POST';
        init.body = JSON.stringify(request.body);
      } else {
        url = `${baseUrl}/api/investigations/${request.investigationId}/stream`;
        init.method = 'GET';
      }

      let resp: Response;
      try {
        resp = await fetch(url, init);
      } catch (err: unknown) {
        if ((err as { name?: string }).name !== 'AbortError') {
          handlers.onStreamError?.(err instanceof Error ? err : new Error(String(err)));
        }
        handlers.onComplete?.();
        return;
      }

      // Pre-flight clarification gate — backend returns 202 + JSON body
      // (not an SSE stream) when the prompt is too vague. ``response.ok``
      // is true for 202 so we branch on the status code explicitly.
      if (resp.status === 202) {
        try {
          const payload = (await resp.json()) as ClarificationRequiredResponse;
          if (payload?.clarification_required === true) {
            handlers.onClarificationRequired?.(payload);
          } else {
            // Defensive: if 202 doesn't carry the expected shape, treat as
            // an HTTP error rather than silently dropping the response.
            handlers.onHttpError?.({
              status: 202,
              detail: 'Unexpected 202 body shape',
            });
          }
        } catch (err: unknown) {
          handlers.onHttpError?.({
            status: 202,
            detail: err instanceof Error ? err.message : String(err),
          });
        }
        handlers.onComplete?.();
        return;
      }

      if (!resp.ok) {
        let detail = `HTTP ${resp.status}`;
        try {
          const errJson = (await resp.json()) as { detail?: string };
          detail = errJson?.detail ?? detail;
        } catch {
          // empty/non-JSON body — keep the HTTP-level message
        }
        if (resp.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          if (window.location.pathname !== '/login') {
            window.location.assign('/login');
          }
        }
        handlers.onHttpError?.({ status: resp.status, detail });
        handlers.onComplete?.();
        return;
      }

      if (!resp.body) {
        handlers.onStreamError?.(new Error('Response body is empty'));
        handlers.onComplete?.();
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      try {
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // SSE frames are separated by a blank line (\n\n).
          let sepIndex: number;
          while ((sepIndex = buffer.indexOf('\n\n')) !== -1) {
            const rawFrame = buffer.slice(0, sepIndex);
            buffer = buffer.slice(sepIndex + 2);
            const parsed = parseSSEFrame(rawFrame);
            if (parsed) handlers.onEvent(parsed);
          }
        }
        // Trailing buffer (if any) — ignore unless it had a final \n\n.
      } catch (err: unknown) {
        if ((err as { name?: string }).name !== 'AbortError') {
          handlers.onStreamError?.(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        handlers.onComplete?.();
      }
    })();

    return () => controller.abort();
  },

  /**
   * Convenience wrapper preserved for callers that already use the live
   * shape. New code should prefer ``stream`` directly with
   * ``mode: 'live'`` for symmetry with the catchup path.
   */
  run: (
    body: InvestigateRequest,
    handlers: InvestigateStreamHandlers,
  ): (() => void) =>
    investigate.stream({ mode: 'live', body }, handlers),
};

// ---------- personal access tokens ----------

import type { PatCreateRequest, PatCreateResponse, PaginatedPats } from '@/types/pat';

export const pats = {
  create: (body: PatCreateRequest) =>
    apiHelper.post<PatCreateResponse>('/api/me/pats/', body),
  list: (params: { page?: number } = {}) =>
    apiHelper.get<PaginatedPats>('/api/me/pats/', params as Record<string, unknown>),
  revoke: (patId: string) => apiHelper.delete<void>(`/api/me/pats/${patId}/`),
};

// ---------- feedback (Sprint 12 Phase A) ----------

import type {
  FeedbackCreate,
  FeedbackOut,
  FeedbackSourceType,
  FeedbackSummary,
} from '@/types/feedback';

/**
 * Feedback endpoints — thumb / tag / note capture for both investigations
 * and analyses. Wire shape is snake_case (mirrors Pydantic schemas).
 *
 * - ``upsert`` is keyed on (source_type, source_id, current user). Re-submit
 *   updates the row in place; the server returns the persisted row.
 * - ``getMine`` returns ``null`` (NOT 404) when the caller has not yet rated
 *   the source — distinct from a missing-source 404.
 * - ``getSummary`` returns aggregated counts plus the caller's own row in a
 *   single round-trip.
 */
export const feedback = {
  /** POST /api/feedback/ — insert or update the caller's row. */
  upsert: (body: FeedbackCreate) =>
    apiHelper.post<FeedbackOut>('/api/feedback/', body),

  /** GET /api/feedback/me/{source_type}/{source_id}/ — caller's row or null. */
  getMine: (sourceType: FeedbackSourceType, sourceId: string) =>
    apiHelper.get<FeedbackOut | null>(
      `/api/feedback/me/${sourceType}/${sourceId}/`,
    ),

  /** GET /api/feedback/{source_type}/{source_id}/summary/ — aggregated counts + caller's row. */
  getSummary: (sourceType: FeedbackSourceType, sourceId: string) =>
    apiHelper.get<FeedbackSummary>(
      `/api/feedback/${sourceType}/${sourceId}/summary/`,
    ),
};

// ---------- memory (admin-only, Sprint 12 Phase D) ----------

import type {
  CaptureGapOut,
  CaptureGapStatus,
  ImprovementDigestOut,
  InfraFactOut,
  InvestigationMemoryOut,
  PaginatedCaptureGaps,
  PaginatedEpisodes,
  PaginatedFacts,
  PaginatedRules,
  ProceduralRuleOut,
  RuleDomain,
  RuleStatus,
  ThumbValue,
} from '@/types/memory';

export interface ListRulesParams {
  /** repeatable; default = proposed,active on the backend. */
  status?: RuleStatus[];
  domain?: RuleDomain;
}

export interface ListCaptureGapsParams {
  /** Defaults to "open" on the backend. */
  status?: CaptureGapStatus;
  page?: number;
  page_size?: number;
}

export interface ListFactsParams {
  /** Defaults to "active" on the backend. */
  status?: 'active' | 'stale' | 'retired';
  topic?: string;
}

export interface ListEpisodesParams {
  q?: string;
  service?: string;
  thumb?: ThumbValue;
  page?: number;
}

/**
 * Memory dashboard endpoints. Every call requires the caller be the admin
 * (settings.admin_email on the backend); non-admin returns 403.
 *
 * Promote/retire side-effects: the backend invalidates its in-process
 * learned_heuristics cache so the next investigation's logged system prompt
 * reflects the change. The FE doesn't need to refetch anything itself
 * beyond the rules list to stay consistent with the dashboard view.
 */
export const memory = {
  listRules: (params: ListRulesParams = {}) => {
    // Axios serialises array params via repeat-key by default; pass
    // through unmodified.
    const wire: Record<string, unknown> = {};
    if (params.status) wire.status = params.status;
    if (params.domain) wire.domain = params.domain;
    return apiHelper.get<PaginatedRules>('/api/memory/rules/', wire);
  },

  promoteRule: (ruleId: string, body: { rule_text: string | null } = { rule_text: null }) =>
    apiHelper.post<ProceduralRuleOut>(`/api/memory/rules/${ruleId}/promote/`, body),

  retireRule: (ruleId: string, body: { rule_text: string | null } = { rule_text: null }) =>
    apiHelper.post<ProceduralRuleOut>(`/api/memory/rules/${ruleId}/retire/`, body),

  listFacts: (params: ListFactsParams = {}) =>
    apiHelper.get<PaginatedFacts>('/api/memory/facts/', params as Record<string, unknown>),

  retireFact: (factId: string) =>
    apiHelper.post<InfraFactOut>(`/api/memory/facts/${factId}/retire/`),

  listEpisodes: (params: ListEpisodesParams = {}) =>
    apiHelper.get<PaginatedEpisodes>(
      '/api/memory/episodes/',
      params as Record<string, unknown>,
    ),

  archiveEpisode: (episodeId: string) =>
    apiHelper.post<InvestigationMemoryOut>(
      `/api/memory/episodes/${episodeId}/archive/`,
    ),

  getLatestDigest: () =>
    apiHelper.get<ImprovementDigestOut>('/api/memory/digest/latest/'),

  listCaptureGaps: (params: ListCaptureGapsParams = {}) =>
    apiHelper.get<PaginatedCaptureGaps>(
      '/api/memory/capture-gaps/',
      params as Record<string, unknown>,
    ),

  setCaptureGapStatus: (id: string, status: CaptureGapStatus) =>
    apiHelper.post<CaptureGapOut>(`/api/memory/capture-gaps/${id}/status/`, { status }),
};

// ---------- intake (Phase 6 — deliver-notion-pm) ----------

import type {
  DetectProject,
  IntakeAnswerItem,
  IntakeAttachment,
  IntakeAttachmentListResponse,
  IntakeDedupeCandidatesResponse,
  IntakeDraftListResponse,
  IntakeSSEEvent,
  IntakeSessionState,
} from '@/types/intake';
import type { RequirementPreview } from '@/types/intake';

// Intake steps that drive LLM rounds (elicitation), embeddings, or Notion
// writes can take 20–40s — well past the 30s default axios timeout. Give them
// generous headroom so the wizard doesn't false-time-out while the backend is
// still working. (get/patch are plain DB reads/updates → default timeout.)
const INTAKE_LLM_TIMEOUT_MS = 120000;

export const intake = {
  /** POST /api/intake/ — start a new intake session (201). LLM-bound. */
  create: (body: { initial_request: string; projects: DetectProject[]; tz?: string }) =>
    apiHelper.post<IntakeSessionState>('/api/intake/', body, { timeout: INTAKE_LLM_TIMEOUT_MS }),

  /** POST /api/intake/{id}/answer/ — submit round answers. LLM-bound.
   *  Pass finalize=true to stop further questioning and synthesize the preview immediately.
   *  Pass idempotency_key + round_index to guard against stale/duplicate submissions. */
  answer: (
    id: string,
    answers: IntakeAnswerItem[],
    finalize = false,
    idempotency_key?: string | null,
    round_index?: number | null,
  ) =>
    apiHelper.post<IntakeSessionState>(
      `/api/intake/${id}/answer/`,
      {
        answers,
        finalize,
        ...(idempotency_key != null ? { idempotency_key } : {}),
        ...(round_index != null ? { round_index } : {}),
      },
      { timeout: INTAKE_LLM_TIMEOUT_MS },
    ),

  /** GET /api/intake/{id}/ — fetch current session state. Default 30s timeout (plain DB read). */
  get: (id: string) =>
    apiHelper.get<IntakeSessionState>(`/api/intake/${id}/`),

  /** PATCH /api/intake/{id}/ — update derived preview fields and/or project
   *  targeting. Stays in preview status. Plain DB write — default 30s timeout. */
  patch: (id: string, body: Partial<RequirementPreview> & { projects?: DetectProject[] }) =>
    apiHelper.patch<IntakeSessionState>(`/api/intake/${id}/`, body),

  /** POST /api/intake/{id}/dedupe-candidates/ — fetch deduplicate candidates. Embeds + cosine. */
  dedupeCandidates: (id: string) =>
    apiHelper.post<IntakeDedupeCandidatesResponse>(`/api/intake/${id}/dedupe-candidates/`, undefined, {
      timeout: INTAKE_LLM_TIMEOUT_MS,
    }),

  /** POST /api/intake/{id}/confirm/ — enqueue confirmation; returns 202 + current
   *  session state. The wizard then polls intake.get() until the confirm step is
   *  terminal. Plain fast call — default timeout (the long work is server-side). */
  confirm: (id: string, body: { link_to_requirement_id: string | null }) =>
    apiHelper.post<IntakeSessionState>(`/api/intake/${id}/confirm/`, body),

  /** GET /api/intake/drafts/ — list open drafts for the current user. Plain DB read — default 30s. */
  listDrafts: () =>
    apiHelper.get<IntakeDraftListResponse>('/api/intake/drafts/'),

  /** DELETE /api/intake/{id}/ — soft-discard (abandon) a session. 204 on success.
   *  409 if already confirmed. Plain DB write — default 30s timeout. */
  discard: (id: string) =>
    apiHelper.delete<void>(`/api/intake/${id}/`),

  /** POST /api/intake/{id}/retry/ — re-run the failed step from persisted state.
   *  409 if no failed step or session is terminal. May be LLM-bound — use INTAKE_LLM_TIMEOUT_MS. */
  retryStep: (id: string) =>
    apiHelper.post<IntakeSessionState>(`/api/intake/${id}/retry/`, undefined, {
      timeout: INTAKE_LLM_TIMEOUT_MS,
    }),

  /** POST /api/intake/{id}/attachments/ — multipart upload (field `file`), 201.
   *  Errors: 400 empty, 403, 404, 409 (not editable / already 10), 413, 415 — all `{detail}`.
   *  The browser sets the multipart boundary; we only pass the FormData. */
  uploadAttachment: (id: string, file: File, onProgress?: (percent: number) => void) => {
    const form = new FormData();
    form.append('file', file);
    return apiHelper.post<IntakeAttachment>(`/api/intake/${id}/attachments/`, form, {
      timeout: INTAKE_LLM_TIMEOUT_MS,
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
  },

  /** DELETE /api/intake/{id}/attachments/{attachmentId}/ — 204; 409 once confirmed. */
  deleteAttachment: (id: string, attachmentId: string) =>
    apiHelper.delete<void>(`/api/intake/${id}/attachments/${attachmentId}/`),
};

/** Attachment bytes (JWT required, so never a bare <a href>). */
export const attachmentFiles = {
  /** GET /api/attachments/{id}/download/?inline=false → Blob. */
  download: (attachmentId: string, inline = false) =>
    apiHelper.get<Blob>(
      `/api/attachments/${attachmentId}/download/`,
      { inline: inline ? 'true' : 'false' },
      { responseType: 'blob' },
    ),
};

// ---------------------------------------------------------------------------
// intake.stream — SSE streaming for Phase C
// ---------------------------------------------------------------------------

/**
 * Three modes for the intake SSE stream:
 *
 *  - `create`: POST /api/intake/stream/ — opens a new session and streams
 *    the extract→question_ready sequence. Used by start() when streaming is enabled.
 *  - `answer`: POST /api/intake/{id}/answer/stream/ — submits round answers
 *    and streams the plan→question_ready→recommend_ready (or synthesize→preview_ready).
 *  - `catchup`: GET /api/intake/{id}/stream/ — replays persisted steps then
 *    tails the live broker channel. Used on rehydrate when a running step is detected.
 */
export type IntakeStreamRequest =
  | { mode: 'create'; body: { initial_request: string; projects: string[]; tz?: string } }
  | {
      mode: 'answer';
      sessionId: string;
      body: {
        answers: IntakeAnswerItem[];
        finalize: boolean;
        idempotency_key?: string | null;
        round_index?: number | null;
      };
    }
  | { mode: 'catchup'; sessionId: string };

/**
 * SSE event handlers for intake.stream.
 * These mirror the shape of InvestigateStreamHandlers from the investigate stream.
 */
export interface IntakeStreamHandlers {
  /** Called for each well-formed SSE event. */
  onEvent: (event: IntakeSSEEvent) => void;
  /**
   * Called when the response is not `text/event-stream` (e.g. proxy buffering,
   * feature off). The caller should fall back to the REST path.
   */
  onNonStream?: (response: Response) => void;
  /**
   * Called on pre-stream HTTP errors (401/403/404/409/422 before stream opens).
   * The caller should surface an inline error and remain on the current screen.
   */
  onHttpError?: (err: { status: number; detail: string }) => void;
  /** Called on mid-stream network errors. */
  onStreamError?: (err: Error) => void;
  /** Called once when the stream closes (success or failure). */
  onComplete?: () => void;
}

/**
 * Open an intake SSE stream and pump events into the supplied handlers.
 * Returns a cancel() function that aborts the in-flight fetch.
 *
 * If the server responds with a non-text/event-stream content-type (e.g.
 * the feature flag is off server-side, a proxy is buffering, or a CDN
 * converts the response), `onNonStream` fires and the caller should fall
 * back to the REST path — no events will be emitted.
 */
export function intakeStream(
  request: IntakeStreamRequest,
  handlers: IntakeStreamHandlers,
): () => void {
  const controller = new AbortController();
  const baseUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
  const token = localStorage.getItem(TOKEN_KEY);

  void (async () => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    let url: string;
    let method: string;
    let body: string | undefined;

    if (request.mode === 'create') {
      url = `${baseUrl}/api/intake/stream/`;
      method = 'POST';
      body = JSON.stringify(request.body);
    } else if (request.mode === 'answer') {
      url = `${baseUrl}/api/intake/${request.sessionId}/answer/stream/`;
      method = 'POST';
      body = JSON.stringify(request.body);
    } else {
      // catchup
      url = `${baseUrl}/api/intake/${request.sessionId}/stream/`;
      method = 'GET';
      body = undefined;
    }

    let resp: Response;
    try {
      resp = await fetch(url, {
        method,
        headers,
        body,
        credentials: 'include',
        signal: controller.signal,
      });
    } catch (err: unknown) {
      if ((err as { name?: string }).name !== 'AbortError') {
        handlers.onStreamError?.(err instanceof Error ? err : new Error(String(err)));
      }
      handlers.onComplete?.();
      return;
    }

    if (!resp.ok) {
      let detail = `HTTP ${resp.status}`;
      try {
        const errJson = (await resp.json()) as { detail?: string };
        detail = errJson?.detail ?? detail;
      } catch {
        // Non-JSON body — keep the HTTP-level message.
      }
      if (resp.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        if (window.location.pathname !== '/login') {
          window.location.assign('/login');
        }
      }
      handlers.onHttpError?.({ status: resp.status, detail });
      handlers.onComplete?.();
      return;
    }

    // If the response is not text/event-stream, signal the caller to fall back.
    const contentType = resp.headers.get('Content-Type') ?? '';
    if (!contentType.includes('text/event-stream')) {
      handlers.onNonStream?.(resp);
      handlers.onComplete?.();
      return;
    }

    if (!resp.body) {
      handlers.onStreamError?.(new Error('Response body is empty'));
      handlers.onComplete?.();
      return;
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE frames are separated by a blank line (\n\n).
        let sepIndex: number;
        while ((sepIndex = buffer.indexOf('\n\n')) !== -1) {
          const rawFrame = buffer.slice(0, sepIndex);
          buffer = buffer.slice(sepIndex + 2);
          const parsed = parseIntakeSSEFrame(rawFrame);
          if (parsed) handlers.onEvent(parsed);
        }
      }
    } catch (err: unknown) {
      if ((err as { name?: string }).name !== 'AbortError') {
        handlers.onStreamError?.(err instanceof Error ? err : new Error(String(err)));
      }
    } finally {
      handlers.onComplete?.();
    }
  })();

  return () => controller.abort();
}

/**
 * Parse a raw SSE frame for intake events.
 * Returns null on malformed/comment frames so the caller can skip them.
 */
function parseIntakeSSEFrame(raw: string): IntakeSSEEvent | null {
  const lines = raw.split('\n');
  let eventName = '';
  const dataLines: string[] = [];
  for (const line of lines) {
    if (line.startsWith(':') || line.length === 0) continue;
    const idx = line.indexOf(':');
    const field = idx === -1 ? line : line.slice(0, idx);
    const valueRaw = idx === -1 ? '' : line.slice(idx + 1);
    const value = valueRaw.startsWith(' ') ? valueRaw.slice(1) : valueRaw;
    if (field === 'event') eventName = value;
    else if (field === 'data') dataLines.push(value);
  }
  if (!eventName || dataLines.length === 0) return null;

  let data: unknown;
  try {
    data = JSON.parse(dataLines.join('\n'));
  } catch {
    return null;
  }

  // Trust the wire shape; the TS union narrows by event name.
  // reason: safe cast — the discriminant is the event name string
  return { event: eventName, data: data as never } as IntakeSSEEvent;
}

// ---------- requirements (Phase 6 — deliver-notion-pm) ----------

import type {
  PaginatedRequirements,
  RequirementOut,
  RequirementsStatsResponse,
  RequirementsThemesResponse,
} from '@/types/requirement';

export interface ListRequirementsParams {
  type?: string;
  severity?: string;
  size?: string;
  area?: string;
  requested_by?: string;
  project?: string;
  q?: string;
  include_archived?: boolean;
  page?: number;
}

export interface ThemesParams {
  min_cluster_size?: number;
  since?: string;
}

export interface StatsParams {
  since?: string;
  bucket?: 'day' | 'week' | 'month';
}

export const requirements = {
  /** GET /api/requirements/{id}/attachments/ — read-only list, any authenticated user. */
  listAttachments: (requirementId: string) =>
    apiHelper.get<IntakeAttachmentListResponse>(`/api/requirements/${requirementId}/attachments/`),

  /** GET /api/requirements/ — filterable paginated list. */
  list: (params: ListRequirementsParams = {}) => {
    const wire: Record<string, unknown> = {};
    if (params.type) wire.type = params.type;
    if (params.severity) wire.severity = params.severity;
    if (params.size) wire.size = params.size;
    if (params.area) wire.area = params.area;
    if (params.requested_by) wire.requested_by = params.requested_by;
    if (params.project) wire.project = params.project;
    if (params.q) wire.q = params.q;
    if (params.include_archived) wire.include_archived = 'true';
    if (params.page && params.page > 1) wire.page = params.page;
    return apiHelper.get<PaginatedRequirements>('/api/requirements/', wire);
  },

  /** GET /api/requirements/themes/ — cluster analysis. */
  themes: (params: ThemesParams = {}) =>
    apiHelper.get<RequirementsThemesResponse>(
      '/api/requirements/themes/',
      params as Record<string, unknown>,
    ),

  /** GET /api/requirements/stats/ — trend statistics. */
  stats: (params: StatsParams = {}) =>
    apiHelper.get<RequirementsStatsResponse>(
      '/api/requirements/stats/',
      params as Record<string, unknown>,
    ),

  /** POST /api/requirements/{id}/archive/ — admin only. */
  archive: (id: string) =>
    apiHelper.post<RequirementOut>(`/api/requirements/${id}/archive/`),

  /** GET /api/requirements/{id}/ — read-only single requirement detail. */
  get: (id: string) => apiHelper.get<RequirementOut>(`/api/requirements/${id}/`),
};

// ---------- pm scorecard (Sprint KPI scorecard) ----------

import type {
  ScorecardResponse,
  ScorecardMetricDetail,
  ScorecardParams,
  ScorecardReadiness,
  ScorecardReadinessParams,
  ScorecardSyncResult,
} from '@/types/scorecard';

/**
 * KPI Scorecard endpoints.
 *
 * getScorecard — summary + all metrics for the selected period.
 * getScorecardMetric — detail for a single metric (breakdowns + tasks + trend).
 *   Called lazily when the user opens the MetricDrawer.
 * getScorecardReadiness — backfill checklist: cohort + per-field + per-task coverage.
 *   Called lazily when the Readiness tab/panel is opened.
 */
export const scorecard = {
  /** GET /api/pm/scorecard/?preset=qtd — (or custom start/end) */
  getScorecard: (params: ScorecardParams = {}) => {
    const wire: Record<string, unknown> = {};
    if (params.preset) wire.preset = params.preset;
    if (params.start) wire.start = params.start;
    if (params.end) wire.end = params.end;
    return apiHelper.get<ScorecardResponse>('/api/pm/scorecard/', wire);
  },

  /** GET /api/pm/scorecard/{metric_key}/?preset=qtd */
  getScorecardMetric: (metricKey: string, params: ScorecardParams = {}) => {
    const wire: Record<string, unknown> = {};
    if (params.preset) wire.preset = params.preset;
    if (params.start) wire.start = params.start;
    if (params.end) wire.end = params.end;
    return apiHelper.get<ScorecardMetricDetail>(
      `/api/pm/scorecard/${metricKey}/`,
      wire,
    );
  },

  /** GET /api/pm/scorecard/readiness/?preset=since_cutoff — (or custom start/end/project) */
  getScorecardReadiness: (params: ScorecardReadinessParams = {}) => {
    const wire: Record<string, unknown> = {};
    if (params.preset) wire.preset = params.preset;
    if (params.start) wire.start = params.start;
    if (params.end) wire.end = params.end;
    if (params.project) wire.project = params.project;
    return apiHelper.get<ScorecardReadiness>('/api/pm/scorecard/readiness/', wire);
  },

  /** POST /api/pm/scorecard/sync/ — admin-only on-demand Notion poll. */
  triggerSync: () =>
    apiHelper.post<ScorecardSyncResult>('/api/pm/scorecard/sync/'),
};

// ---------- pm notifications + ruleset (pm-notify-brain) ----------

import type {
  PaginatedPmNotifications,
  PaginatedPmPipelineWrites,
  PmNotificationStatus,
  PmPipelineWriteStatus,
  PmPipelineWriteSummary,
  PmRuleset,
} from '@/types/pm_notification';

export interface ListPmNotificationsParams {
  status?: PmNotificationStatus;
  notification_type?: string;
  notion_id?: string;
  page?: number;
}

export interface ListPmPipelineWritesParams {
  status?: PmPipelineWriteStatus;
  notion_id?: string;
  page?: number;
}

/**
 * PM notification log + ruleset endpoints.
 *
 * notifications.list — ADMIN-only (backend enforces 403 for non-admins).
 * ruleset.get        — any authenticated user.
 * ruleset.update     — ADMIN-only.
 */
export const pmNotifications = {
  /** GET /api/pm/notifications/ — paginated notification log, failures-first. */
  list: (params: ListPmNotificationsParams = {}) => {
    const wire: Record<string, unknown> = {};
    if (params.status) wire.status = params.status;
    if (params.notification_type) wire.notification_type = params.notification_type;
    if (params.notion_id) wire.notion_id = params.notion_id;
    if (params.page && params.page > 1) wire.page = params.page;
    return apiHelper.get<PaginatedPmNotifications>('/api/pm/notifications/', wire);
  },
};

/**
 * Task Pipeline writeback audit log — ADMIN-only (backend enforces 403).
 *
 * Rows appear only when the Ticket-derived stage disagreed with the board, so
 * this is a log of changes, not a per-run dump.
 */
export const pmPipelineWrites = {
  /** GET /api/pm/pipeline-writes/ — paginated decisions, newest first. */
  list: (params: ListPmPipelineWritesParams = {}) => {
    const wire: Record<string, unknown> = {};
    if (params.status) wire.status = params.status;
    if (params.notion_id) wire.notion_id = params.notion_id;
    if (params.page && params.page > 1) wire.page = params.page;
    return apiHelper.get<PaginatedPmPipelineWrites>('/api/pm/pipeline-writes/', wire);
  },

  /** GET /api/pm/pipeline-writes/summary/ — counts per status + launch switch. */
  summary: () =>
    apiHelper.get<PmPipelineWriteSummary>('/api/pm/pipeline-writes/summary/'),
};

export const pmRuleset = {
  /** GET /api/pm/ruleset/ — retrieve the currently active ruleset. */
  get: () => apiHelper.get<PmRuleset>('/api/pm/ruleset/'),

  /** PUT /api/pm/ruleset/ — save a new version (ADMIN-only). */
  update: (body: string) =>
    apiHelper.put<PmRuleset>('/api/pm/ruleset/', { body }),
};

import type { TaskTimeline, TimelineHealth } from '@/types/pm_stage_event';

/**
 * pmTimeline — the stage-transition recorder's read surface.
 *
 * getHealth — recorder liveness: last event per source, 24h activity, coverage.
 *   This is what distinguishes "nothing moved today" from "the recorder died".
 * getTaskTimeline — every transition observed for one page, oldest first.
 *
 * Both are admin-only; the backend returns 403 for non-admins and this
 * propagates the detail string for the page to render.
 */
export const pmTimeline = {
  /** GET /api/pm/timeline/health/ */
  getHealth: () => apiHelper.get<TimelineHealth>('/api/pm/timeline/health/'),

  /** GET /api/pm/timeline/{notion_id}/ */
  getTaskTimeline: (notionId: string) =>
    apiHelper.get<TaskTimeline>(`/api/pm/timeline/${encodeURIComponent(notionId)}/`),
};

// ---------- teams (Phase 2 — deliver-notion-pm) ----------

import type {
  PaginatedTeamMembers,
  PaginatedTeams,
  SeedRosterResponse,
  Team,
  TeamCreateBody,
  TeamMember,
  TeamMemberCreateBody,
} from '@/types/team';

export const teams = {
  /** GET /api/teams/ — paginated list of teams. */
  listTeams: (params: { page?: number } = {}) =>
    apiHelper.get<PaginatedTeams>('/api/teams/', params as Record<string, unknown>),

  /** GET /api/teams/{team_id}/members/ — members of a single team. */
  listMembers: (teamId: string, params: { page?: number } = {}) =>
    apiHelper.get<PaginatedTeamMembers>(
      `/api/teams/${teamId}/members/`,
      params as Record<string, unknown>,
    ),

  /** POST /api/teams/ — create a team. Returns 201. */
  createTeam: (body: TeamCreateBody) =>
    apiHelper.post<Team>('/api/teams/', body),

  /** POST /api/teams/{team_id}/members/ — add a member. Returns 201. */
  createMember: (teamId: string, body: TeamMemberCreateBody) =>
    apiHelper.post<TeamMember>(`/api/teams/${teamId}/members/`, body),

  /** PUT /api/teams/{team_id}/ — partial update a team. */
  updateTeam: (teamId: string, body: Partial<TeamCreateBody>) =>
    apiHelper.put<Team>(`/api/teams/${teamId}/`, body),

  /** PUT /api/teams/{team_id}/members/{member_id}/ — partial update a member. */
  updateMember: (teamId: string, memberId: string, body: Partial<TeamMemberCreateBody>) =>
    apiHelper.put<TeamMember>(`/api/teams/${teamId}/members/${memberId}/`, body),

  /** DELETE /api/teams/{team_id}/members/{member_id}/ — returns 204. */
  deleteMember: (teamId: string, memberId: string) =>
    apiHelper.delete<void>(`/api/teams/${teamId}/members/${memberId}/`),

  /** DELETE /api/teams/{team_id}/ — returns 204. 409 if active members remain. */
  deleteTeam: (teamId: string) =>
    apiHelper.delete<void>(`/api/teams/${teamId}/`),

  /** POST /api/teams/roster/seed/ — seed the roster from Notion. */
  seedRoster: () =>
    apiHelper.post<SeedRosterResponse>('/api/teams/roster/seed/'),
};

// ---------- reviews (Sherlock Reviewer — P10) ----------

import type {
  FindingFeedbackRequest,
  PaginatedReviewRepoConfigs,
  PaginatedReviews,
  PrReviewDetail,
  ReviewFindingPublic,
  ReviewRepoConfigCreate,
  ReviewRepoConfigOut,
  ReviewRepoConfigPatch,
  ReviewSSEEvent,
  ReviewStatus,
  ReviewTriggerRequest,
  ReviewTriggerResponse,
  Verdict,
} from '@/types/review';

export interface ListReviewsParams {
  page?: number;
  repo?: string;
  status?: ReviewStatus;
  verdict?: Verdict;
}

/**
 * Catchup-only SSE stream handlers — GET /api/reviews/{id}/stream. Unlike
 * `investigate.stream` there is no `live` mode: a PrReview row always
 * exists before the FE ever opens the stream (created by the GitHub
 * webhook or the trigger task), so there's nothing to POST-to-start.
 */
export interface ReviewStreamHandlers {
  onEvent: (event: ReviewSSEEvent) => void;
  onHttpError?: (err: { status: number; detail: string }) => void;
  onStreamError?: (err: Error) => void;
  onComplete?: () => void;
}

export const reviews = {
  /** GET /api/reviews/ — paginated, filterable by repo/status/verdict. */
  list: (params: ListReviewsParams = {}) => {
    const wire: Record<string, unknown> = {};
    if (params.page && params.page > 1) wire.page = params.page;
    if (params.repo) wire.repo = params.repo;
    if (params.status) wire.status = params.status;
    if (params.verdict) wire.verdict = params.verdict;
    return apiHelper.get<PaginatedReviews>('/api/reviews/', wire);
  },

  /** GET /api/reviews/{id}/ — full detail incl. findings + tool_calls. */
  get: (id: string) => apiHelper.get<PrReviewDetail>(`/api/reviews/${id}/`),

  /**
   * POST /api/reviews/{review_id}/findings/{finding_id}/feedback/ — thumb
   * (+1/-1) + optional note. Any authenticated user may call this.
   */
  submitFindingFeedback: (
    reviewId: string,
    findingId: string,
    body: FindingFeedbackRequest,
  ) =>
    apiHelper.post<ReviewFindingPublic>(
      `/api/reviews/${reviewId}/findings/${findingId}/feedback/`,
      body,
    ),

  /** POST /api/reviews/trigger — admin-only manual command review, 202. */
  trigger: (body: ReviewTriggerRequest) =>
    apiHelper.post<ReviewTriggerResponse>('/api/reviews/trigger', body),

  repos: {
    /** GET /api/reviews/repos/ — any authed user. */
    list: () => apiHelper.get<PaginatedReviewRepoConfigs>('/api/reviews/repos/'),

    /** POST /api/reviews/repos/ — admin-only, 201. */
    create: (body: ReviewRepoConfigCreate) =>
      apiHelper.post<ReviewRepoConfigOut>('/api/reviews/repos/', body),

    /** PATCH /api/reviews/repos/{id}/ — admin-only partial update. */
    update: (id: string, body: ReviewRepoConfigPatch) =>
      apiHelper.patch<ReviewRepoConfigOut>(`/api/reviews/repos/${id}/`, body),
  },

  /**
   * Open the catchup SSE stream and pump events into the supplied
   * handlers. Returns a teardown function that aborts the in-flight
   * fetch. Mirrors `investigate.stream`'s fetch()+ReadableStream
   * implementation — EventSource can't set the Authorization header.
   */
  stream: (reviewId: string, handlers: ReviewStreamHandlers): (() => void) => {
    const controller = new AbortController();
    const baseUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
    const token = localStorage.getItem(TOKEN_KEY);

    void (async () => {
      let resp: Response;
      try {
        resp = await fetch(`${baseUrl}/api/reviews/${reviewId}/stream`, {
          method: 'GET',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            Accept: 'text/event-stream',
          },
          credentials: 'include',
          signal: controller.signal,
        });
      } catch (err: unknown) {
        if ((err as { name?: string }).name !== 'AbortError') {
          handlers.onStreamError?.(err instanceof Error ? err : new Error(String(err)));
        }
        handlers.onComplete?.();
        return;
      }

      if (!resp.ok) {
        let detail = `HTTP ${resp.status}`;
        try {
          const errJson = (await resp.json()) as { detail?: string };
          detail = errJson?.detail ?? detail;
        } catch {
          // empty/non-JSON body — keep the HTTP-level message
        }
        if (resp.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          if (window.location.pathname !== '/login') {
            window.location.assign('/login');
          }
        }
        handlers.onHttpError?.({ status: resp.status, detail });
        handlers.onComplete?.();
        return;
      }

      if (!resp.body) {
        handlers.onStreamError?.(new Error('Response body is empty'));
        handlers.onComplete?.();
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      try {
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // SSE frames are separated by a blank line (\n\n).
          let sepIndex: number;
          while ((sepIndex = buffer.indexOf('\n\n')) !== -1) {
            const rawFrame = buffer.slice(0, sepIndex);
            buffer = buffer.slice(sepIndex + 2);
            const parsed = parseReviewSSEFrame(rawFrame);
            if (parsed) handlers.onEvent(parsed);
          }
        }
      } catch (err: unknown) {
        if ((err as { name?: string }).name !== 'AbortError') {
          handlers.onStreamError?.(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        handlers.onComplete?.();
      }
    })();

    return () => controller.abort();
  },
};

// ---------- integrations ----------

import type {
  IntegrationStatus,
  IntegrationStopResponse,
  IntegrationUpdate,
} from '@/types/integrations';

export const integrations = {
  /** GET /api/integrations/ — any authed user. */
  list: () => apiHelper.get<IntegrationStatus[]>('/api/integrations/'),
  /** PUT /api/integrations/{adapter}/ — admin-only. */
  update: (adapter: string, body: IntegrationUpdate) =>
    apiHelper.put<IntegrationStatus>(`/api/integrations/${adapter}/`, body),
  /** POST /api/integrations/{adapter}/stop/ — admin-only; disables AND drops open turns. */
  stop: (adapter: string) =>
    apiHelper.post<IntegrationStopResponse>(`/api/integrations/${adapter}/stop/`),
};

/**
 * Parse a single raw SSE frame ("event: <name>\ndata: <json>") into a
 * ReviewSSEEvent. Returns null on malformed/comment frames so the caller
 * can skip them without throwing. Mirrors `parseSSEFrame` below.
 */
function parseReviewSSEFrame(raw: string): ReviewSSEEvent | null {
  const lines = raw.split('\n');
  let eventName = '';
  const dataLines: string[] = [];
  for (const line of lines) {
    if (line.startsWith(':') || line.length === 0) continue;
    const idx = line.indexOf(':');
    const field = idx === -1 ? line : line.slice(0, idx);
    const valueRaw = idx === -1 ? '' : line.slice(idx + 1);
    const value = valueRaw.startsWith(' ') ? valueRaw.slice(1) : valueRaw;
    if (field === 'event') eventName = value;
    else if (field === 'data') dataLines.push(value);
  }
  if (!eventName || dataLines.length === 0) return null;

  let data: unknown;
  try {
    data = JSON.parse(dataLines.join('\n'));
  } catch {
    return null;
  }

  // Trust the wire shape; the TS union narrows by event name.
  return { event: eventName, data: data as never } as ReviewSSEEvent;
}

/**
 * Parse a single raw SSE frame ("event: <name>\ndata: <json>") into an
 * SSEEvent. Returns null on malformed/comment frames so the caller can
 * skip them without throwing.
 */
function parseSSEFrame(raw: string): SSEEvent | null {
  const lines = raw.split('\n');
  let eventName = '';
  const dataLines: string[] = [];
  for (const line of lines) {
    if (line.startsWith(':') || line.length === 0) continue;
    const idx = line.indexOf(':');
    const field = idx === -1 ? line : line.slice(0, idx);
    const valueRaw = idx === -1 ? '' : line.slice(idx + 1);
    const value = valueRaw.startsWith(' ') ? valueRaw.slice(1) : valueRaw;
    if (field === 'event') eventName = value;
    else if (field === 'data') dataLines.push(value);
  }
  if (!eventName || dataLines.length === 0) return null;

  let data: unknown;
  try {
    data = JSON.parse(dataLines.join('\n'));
  } catch {
    return null;
  }

  // Trust the wire shape; the TS union narrows by event name.
  return { event: eventName, data: data as never } as SSEEvent;
}
